import React, { useRef, useState, useEffect } from 'react';
import RankedRows from './RankedRows';
import { SubscriptionSummary } from '../../../../types/subscription';
import { SortingToggleOptions } from '../../types';
import {
  OverviewChartsContext,
  useChartsContext,
} from '../../ClusterItem/Charts/context';
import PieChart from '../../ClusterItem/Charts/PieChart';
import { getBotColor } from '../../ClusterItem/Charts/helpers';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectOverviewChartPercentage,
  setOverviewChartPercentage,
} from '../../../../store/slices/ui';
import './index.css';

type ChartStyle = 'Spokes' | 'Ranked';
const CHART_STYLE_OPTIONS: ChartStyle[] = ['Spokes', 'Ranked'];

const CHART_DATASETS: Array<{
  title: string;
  option: SortingToggleOptions;
  signed: boolean;
}> = [
  { title: 'Diversity', option: SortingToggleOptions.Diversity, signed: false },
  {
    title: 'Total Stable Profit',
    option: SortingToggleOptions.TotalStableProfit,
    signed: true,
  },
  {
    title: 'Total Asset Profit',
    option: SortingToggleOptions.TotalAssetProfit,
    signed: true,
  },
  {
    title: 'No Algorithmic Difference',
    option: SortingToggleOptions.NoAlgorithmicDifference,
    signed: true,
  },
  {
    title: 'Current Stable Profit',
    option: SortingToggleOptions.CurrentStableProfit,
    signed: true,
  },
  {
    title: 'Current Asset Profit',
    option: SortingToggleOptions.CurrentAssetProfit,
    signed: true,
  },
];

// ── PieCard — renders PieChart at measured size ──────────────────────────────

const PieCard: React.FC<{ title: string; option: SortingToggleOptions }> = ({
  title,
  option,
}) => {
  const { getChartData } = useChartsContext();
  const data = getChartData(option);
  const ref = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(0);

  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return;
    const measure = () => {
      if (!bodyRef.current) return;
      const w = bodyRef.current.clientWidth;
      const h = bodyRef.current.clientHeight;
      // Square that fits comfortably in the body, with a small margin to
      // avoid clipping outer ring stroke.
      const dim = Math.max(40, Math.min(w, h) - 8);
      setSize(dim);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="chart-card" ref={ref}>
      <span
        className="chart-card-title"
        style={{ textAlign: 'center', width: '100%' }}
      >
        {title}
      </span>
      <div
        ref={bodyRef}
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        {size > 0 && (
          <PieChart
            data={data}
            activeToggleOption={option}
            showAllData={true}
            hideLabels={true}
            width={size}
            height={size}
          />
        )}
      </div>
    </div>
  );
};

// ── Inner content (needs context) ────────────────────────────────────────────

const ChartsSectionInner: React.FC = () => {
  const dispatch = useDispatch();
  const overviewChartPercentage = useSelector(selectOverviewChartPercentage);
  const [chartStyle, setChartStyle] = React.useState<ChartStyle>('Spokes');
  const { getChartData } = useChartsContext();

  const handlePercentageClick = () =>
    dispatch(setOverviewChartPercentage(!overviewChartPercentage));

  return (
    <div className="charts-container">
      {/* ── Header ── */}
      <div className="charts-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="charts-title">Profit Composition</span>
          <span
            className="charts-badge"
            onClick={handlePercentageClick}
            style={{
              cursor: 'pointer',
              backgroundColor: overviewChartPercentage ? '#fff' : undefined,
              color: overviewChartPercentage ? '#000' : undefined,
            }}
          >
            %
          </span>
        </div>
        <div className="pill-toggle">
          {CHART_STYLE_OPTIONS.map((opt) => (
            <button
              key={opt}
              className={`pill-toggle-btn${
                chartStyle === opt ? ' pill-toggle-btn--active' : ''
              }`}
              onClick={() => setChartStyle(opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="charts-grid">
        {CHART_DATASETS.map((ds) => {
          const chartData = getChartData(ds.option);
          const isRanked = chartStyle === 'Ranked';

          if (isRanked) {
            const rankedData = (chartData ?? []).map((d: any) => ({
              sym: d.x,
              color: getBotColor(d.x),
              value: d.value ?? d.y,
            }));

            const agg = rankedData.reduce(
              (a: number, b: any) => a + (b.value || 0),
              0,
            );
            const aggColor = agg >= 0 ? '#00B061' : '#F0162F';
            const aggLabel = overviewChartPercentage
              ? (agg >= 0 ? '+' : '') +
                (agg * 100).toFixed(2).replace('.', ',') +
                '%'
              : (agg >= 0 ? '+' : '-') + '$' + Math.abs(agg).toFixed(0);

            return (
              <div key={ds.title} className="chart-card chart-card--ranked">
                <div className="chart-card-header">
                  <span className="chart-card-title">{ds.title}</span>
                  {ds.signed && (
                    <span
                      className="chart-card-agg"
                      style={{ color: aggColor }}
                    >
                      {aggLabel}
                    </span>
                  )}
                </div>
                <div className="chart-card-body">
                  <RankedRows data={rankedData} signed={ds.signed} />
                </div>
              </div>
            );
          }

          return <PieCard key={ds.title} title={ds.title} option={ds.option} />;
        })}
      </div>
    </div>
  );
};

// ── ChartsSection ────────────────────────────────────────────────────────────

interface ChartsSectionProps {
  subscriptions: SubscriptionSummary[];
  totalValue: number;
}

const ChartsSection: React.FC<ChartsSectionProps> = ({
  subscriptions,
  totalValue,
}) => {
  return (
    <OverviewChartsContext
      totalValue={totalValue}
      subscriptions={subscriptions}
    >
      <ChartsSectionInner />
    </OverviewChartsContext>
  );
};

export default ChartsSection;
